import { PencilIcon } from '@icons'
import { DOCTOR_MODAL_TYPE, useModalStore } from '@modal'
import { MARResMock } from '@type/mar.type'
import { Badge, Button, SideDrawer } from '@ui'
import InformationList from '@ui/InformationList'
import { capitalizeDrugName, concatenateStrengthsConcise, formatTo12Hour, isAllowed } from '@util'
import { Box, Text } from 'grommet'
import { useState } from 'react'

type Props = {
  onClose: () => void
  isOpen: boolean
  id?: string
  defaultData?: MARResMock
}

const detailOptions = ['medication', 'administration', 'notes']

export const MARDetails = ({ defaultData, id, onClose }: Props) => {
  const { openModal } = useModalStore()
  const [tab, setTab] = useState('medication')

  const medicationInfo = {
    comment: defaultData?.meta?.last_transition?.metadata?.note || '-',
    dosage: defaultData?.attributes.medication_details.dosage || '-',
    frequency: defaultData?.attributes.medication_details.medication_frequency_code || '-',
    medication: capitalizeDrugName(defaultData?.attributes.medication_details.drug_name) || '-',
    quantity: defaultData?.attributes.medication_details.dose_quantity || '-',
    form: defaultData?.attributes.medication_details.form || '-',
    strength:
      defaultData?.attributes.medication_details.strength ||
      concatenateStrengthsConcise(defaultData?.attributes.medication_details.api_doses) ||
      '-'
  }

  const administrationInfo = {
    'Mode of Administration': defaultData?.attributes.medication_details.route || '-',
    'Period of Entity': defaultData?.attributes.period_of_day || '-',
    Status: defaultData?.meta.status || '-',
    'Time of Administration': defaultData?.attributes.administered_at
      ? formatTo12Hour(defaultData?.attributes.administered_at)
      : '-'
  }

  const notesInfo = {
    Note: defaultData?.meta?.last_transition?.metadata?.note || '-'
  }

  const header = (
    <Box gap={'20px'} margin={{ vertical: '16px' }}>
      <Box align="center" direction="row" gap={'8px'}>
        <Text size="24px" weight={600}>
          {capitalizeDrugName(defaultData?.attributes.medication_details.drug_name)}
        </Text>
        <Badge label={defaultData?.meta.status} status={defaultData?.meta.status} />
      </Box>
      {defaultData &&
        isAllowed(defaultData?.attributes.scheduled_for) &&
        defaultData.meta.status.toLowerCase() === 'pending' && (
          <div className="max-w-[157px]">
            <Button
              icon={<PencilIcon />}
              kinds={'secondary'}
              label="Edit"
              onClick={() => openModal(DOCTOR_MODAL_TYPE.EDIT_MAR_MODAL, { defaultData: defaultData?.attributes, id })}
              size={'sm'}
            />
          </div>
        )}
    </Box>
  )

  return (
    <SideDrawer
      header={header}
      onClose={onClose}
      options={detailOptions}
      setTab={setTab}
      style={{ padding: '0 40px' }}
      title="M.A.R Details"
    >
      <div className="overflow-auto px-5 pb-6">
        {tab === 'medication' && <InformationList data={medicationInfo} />}
        {tab === 'administration' && <InformationList data={administrationInfo} keyStyle={{ textTransform: 'none' }} />}
        {tab === 'notes' && <InformationList data={notesInfo} />}
      </div>
    </SideDrawer>
  )
}
